import { HttpClient, HttpContext } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { Observable } from 'rxjs'
import {
  catchError,
  firstValueFrom,
  map,
  tap,
  throwError,
} from 'rxjs'
import { APIEnvelope } from 'src/app/shared/models/api'
import { PaginationParams, FilterParams, SortParams } from 'src/app/shared/utilities/http-params'
import {
  httpParams,
  pagination,
  sort,
  withHttpParams,
  filter,
} from 'src/app/shared/utilities/http-params'
import { MDMABestAvailable, MDMAJobStatus, MDMAPreviousRun } from './mdma-settlements.types'
import { GLOBAL_LOADING_SPINNER_ENABLED } from 'src/app/shared/interceptors/loading-spinner-interceptor'
import { PARSE_DATE_PROPS } from 'src/app/shared/interceptors/date-interceptor'
import { format } from 'date-fns'
import { DownloadService } from 'src/app/shared/services/download.service'
import { ToastService } from '../toasts/toasts.service'
import { dateOnlyFormat, formatAsUTC } from '../../shared/utilities/date'

@Injectable({ providedIn: 'root' })
export class MDMASettlementsService {
  private http = inject(HttpClient)
  private toast = inject(ToastService)
  private downloadService = inject(DownloadService)

  runMDMA(
    operatingDate: Date,
    settlementType: string,
  ): Observable<unknown> {
    return this.http
      .post('mdma_aggregation', {
        operating_day: format(
          operatingDate,
          dateOnlyFormat,
        ),
        settlement_type: settlementType,
      })
  }

  publishMDMA(target_run_instance_uid: string, unpublish_similar: boolean): Observable<unknown> {
    return this.http
      .post('mdma_aggregation/publish', {
        target_run_instance_uid,
        unpublish_similar,
      })
      .pipe(
        catchError((e) => {
          this.toast.show({
            message: 'An unexpected error occurred!',
            type: 'error',
          })
          return throwError(() => e)
        }),
      )
  }

  unpublishMDMA(run_instance_uid: string): Observable<unknown> {
    return this.http
      .put(`mdma_aggregation/${run_instance_uid}/unpublish`, {})
      .pipe(
        catchError((e) => {
          this.toast.show({
            message: 'An unexpected error occurred!',
            type: 'error',
          })
          return throwError(() => e)
        }),
      )
  }

  getMDMAJobs(
    page?: PaginationParams,
    filters?: readonly FilterParams[],
    order?: readonly SortParams[],
  ): Observable<APIEnvelope<MDMAJobStatus>> {
    const params = httpParams(
      withHttpParams(pagination, page),
      withHttpParams(filter, filters),
      withHttpParams(sort, order),
    )
    return this.http.get<APIEnvelope<MDMAJobStatus>>(
      'mdma_aggregation/job_status',
      {
        params,
        context: new HttpContext()
          .set(GLOBAL_LOADING_SPINNER_ENABLED, false)
          .set(PARSE_DATE_PROPS, ['operating_date', 'start_time', 'end_time', 'create_time']),
      },
    )
  }

  bestAvailableMDMA(operating_day: Date, settlement_type_uid: string): Observable<MDMABestAvailable> {
    const params = httpParams(
      withHttpParams(filter, [
        { name: 'operating_day', values: [{ value: formatAsUTC(operating_day, false) }] },
        { name: 'settlement_type_uid', values: [{ value: settlement_type_uid }] },
      ]),
    )
    return this.http.get<{ result: MDMABestAvailable }>(
      'mdma_aggregation/best-available',
      {
        params,
        context: new HttpContext()
          .set(GLOBAL_LOADING_SPINNER_ENABLED, true)
          .set(PARSE_DATE_PROPS, ['operating_day', 'start_time', 'end_time']),
      },
    ).pipe(map(_ => _.result))
  }

  getPreviousRunMDMA(runInstanceId: string): Observable<MDMAPreviousRun | undefined> {
    return this.http.get<APIEnvelope<MDMAPreviousRun>>(
      `mdma_aggregation/${runInstanceId}/previous`,
      {
        context: new HttpContext()
          .set(GLOBAL_LOADING_SPINNER_ENABLED, true)
          .set(PARSE_DATE_PROPS, [
            'operating_day',
            'prev_settlement_type_operating_date',
            'prev_operating_date_operating_date',
          ]),
      },
    ).pipe(map(_ => _.results[0]))
  }

  getPublishedMDMAFiles(runInstanceId: string): Observable<readonly string[]> {
    return this.http.get<APIEnvelope<string>>(
      `mdma_aggregation/${runInstanceId}/published-files`,
      { context: new HttpContext().set(GLOBAL_LOADING_SPINNER_ENABLED, true) },
    ).pipe(map(_ => _.results))
  }

  async downloadPublishedMDMAFile(run_instance_id: string, file_name: string) {
    return await firstValueFrom(
      this.http
        .get<APIEnvelope<string>>(
          `mdma_aggregation/${run_instance_id}/published-files/${file_name}`,
          {
            context: new HttpContext().set(
              GLOBAL_LOADING_SPINNER_ENABLED,
              true,
            ),
          },
        )
        .pipe(
          map((res) => res.results[0]),
          tap((file) => {
            if (file) {
              this.downloadService.download([{ url: file, name: file_name }])
            }
          }),
        ),
    )
  }
}
